import React from 'react';
import {
  Zap,
  Truck,
  Package,
  Gauge,
  Wrench,
  HardHat,
  FlaskConical,
  Activity,
  Cpu,
  Container,
  Fan,
  Users,
  ShieldCheck,
  FileCheck2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building2,
  Factory,
  Flame,
  Layers3,
  ClipboardCheck,
  Timer,
  Handshake,
} from 'lucide-react';

import {
  usePageSeo,
  Section,
  SectionHeading,
  CompanyHero,
  FeatureCard,
  StatCard,
  ListPanel,
  AssuranceStrip,
  CTABanner,
  GroupMemberSection,
} from '../../components/company/CompanyBlocks';
import { SeoHead } from '../../components/ui/SeoHead';
import { Layout } from '../../components/layout/Layout';

/* ================================================================== *
 *  PETRONIX ENERGY SERVICES (EGYPT) — /companies/petronix
 *  Built on the EnergiPro Solutions capability model, repositioned
 *  for the Egyptian energy and industrial market.
 *
 *  NOTE FOR RAED: contact block and the two "scale" stat cards are
 *  marked PLACEHOLDER — replace with the registered Petronix entity
 *  details before publishing.
 * ================================================================== */

const coreServices = [
  {
    icon: <Package className="w-7 h-7" />,
    label: 'Supply',
    title: 'Equipment Rental & Industrial Supply',
    body:
      'Short and long-term rental of process equipment, generators, compressors, pumps and tanks, backed by an industrial supply desk for consumables, spares and specified materials against client procurement standards.',
    tags: ['Rental', 'Spares', 'Consumables'],
  },
  {
    icon: <Gauge className="w-7 h-7" />,
    label: 'Process',
    title: 'Process Equipment & Fluid Handling',
    body:
      'Separators, filtration and treatment skids, transfer and injection pumps, manifolds and flow lines for production, testing, flowback and industrial fluid transfer duties.',
    tags: ['Separation', 'Filtration', 'Transfer'],
  },
  {
    icon: <HardHat className="w-7 h-7" />,
    label: 'Manpower',
    title: 'Technical Manpower Services',
    body:
      'Certified operators, technicians, supervisors, mechanical and electrical fitters, riggers and HSE personnel supplied against defined competency matrices for campaign, call-off or long-term assignment.',
    tags: ['Operators', 'Supervisors', 'HSE'],
  },
  {
    icon: <Wrench className="w-7 h-7" />,
    label: 'Field',
    title: 'Field Services & Maintenance Support',
    body:
      'Rig-up and rig-down crews, routine and corrective maintenance, on-site repairs, equipment overhaul and standby cover — delivered under the client permit-to-work system.',
    tags: ['Rig-Up', 'Maintenance', 'Standby'],
  },
  {
    icon: <Timer className="w-7 h-7" />,
    label: 'Turnaround',
    title: 'Shutdown & Commissioning Support',
    body:
      'Planned shutdown and turnaround resourcing, pre-commissioning, flushing, purging, leak testing and commissioning assistance, sequenced against the client\'s critical path.',
    tags: ['Shutdown', 'Pre-Comm', 'Commissioning'],
  },
  {
    icon: <Truck className="w-7 h-7" />,
    label: 'Logistics',
    title: 'Mobilisation & Field Logistics',
    body:
      'Heavy haul, low-bed and crane truck movements, customs and site-gate coordination, warehousing and staged mobilisation to remote and desert locations.',
    tags: ['Heavy Haul', 'Customs', 'Warehousing'],
  },
];

const industries = [
  { icon: <Flame className="w-6 h-6" />, title: 'Upstream Production', body: 'Onshore oil and gas production operations requiring equipment, crews and consumables at the wellsite and gathering facility.' },
  { icon: <Gauge className="w-6 h-6" />, title: 'Drilling & Workover', body: 'Rig support scopes — rental equipment, technical manpower, fluid handling and site logistics for drilling and workover campaigns.' },
  { icon: <Activity className="w-6 h-6" />, title: 'Well Testing & Flowback', body: 'Separation, storage, measurement and reporting support for production testing, cleanup and extended well test operations.' },
  { icon: <Factory className="w-6 h-6" />, title: 'Field Gas Processing', body: 'Field gas treatment, dehydration and processing facilities requiring rental packages, maintenance crews and turnaround resourcing.' },
  { icon: <Layers3 className="w-6 h-6" />, title: 'EPC & Construction', body: 'Equipment, manpower and logistics packages supporting EPC contractors on oil and gas construction, pre-commissioning and handover.' },
  { icon: <Handshake className="w-6 h-6" />, title: 'Oilfield Service Companies', body: 'Subcontract capacity to service companies needing additional fleet, crews or field logistics on a campaign or call-off basis.' },
  { icon: <Building2 className="w-6 h-6" />, title: 'Operators & Asset Owners', body: 'Concession holders and asset owners requiring long-term maintenance resourcing, integrity support and inspection coordination.' },
  { icon: <Truck className="w-6 h-6" />, title: 'Remote Field Locations', body: 'Western Desert, Eastern Desert, Delta and Gulf of Suez onshore locations requiring staged mobilisation and site-gate coordination.' },
];

const capabilities = [
  {
    title: 'Equipment & Rental Fleet',
    items: [
      'Generators and temporary power packages',
      'Air compressors and air treatment',
      'Storage and process tanks, tank solutions',
      'Pumps, manifolds and fluid transfer packages',
      'Filtration, separation and treatment skids',
      'Lifting, access and general site equipment',
    ],
    icon: <Container className="w-5 h-5" />,
  },
  {
    title: 'Technical & Digital Services',
    items: [
      'Oilfield and industrial chemicals supply',
      'Instrumentation supply and field calibration',
      'Flow measurement and metering support',
      'Digital field solutions and remote data capture',
      'Data acquisition and reporting to client format',
      'Asset integrity and inspection coordination',
    ],
    icon: <Cpu className="w-5 h-5" />,
  },
  {
    title: 'Project Support Services',
    items: [
      'EPC support packages — equipment, crews, logistics',
      'Industrial and mechanical maintenance resourcing',
      'Shutdown, turnaround and campaign support',
      'Pre-commissioning and commissioning assistance',
      'Mobilisation, demobilisation and field logistics',
      'Engineering support and technical documentation',
    ],
    icon: <ClipboardCheck className="w-5 h-5" />,
  },
];

const projects = [
  { title: 'Well Testing & Flowback Support', meta: 'Onshore campaign support', body: 'Separation, storage and measurement equipment supplied with operating crews for production testing and flowback campaigns, with data delivered to the client reporting standard.' },
  { title: 'Facility Shutdown Resourcing', meta: 'Gas plant & production facility turnaround', body: 'Planned turnaround support providing mechanical fitters, riggers, supervisors and HSE personnel against a fixed shutdown window and permit-to-work regime.' },
  { title: 'Temporary Power & Compressed Air', meta: 'Field facility continuity', body: 'Generator and compressor packages installed, fuelled, maintained and manned for the duration of a planned outage and subsequent restart.' },
  { title: 'Tank Farm & Fluid Handling', meta: 'Storage and transfer scope', body: 'Storage tanks, transfer pumps, manifolds and filtration supplied and operated for bulk fluid handling, cleaning and transfer duties.' },
  { title: 'Pipeline Purging & Pressure Testing', meta: 'Pre-commissioning scope', body: 'Nitrogen purging, flushing, leak testing and certified chart delivery as part of pre-commissioning support to a construction contractor.' },
  { title: 'EPC Construction Support', meta: 'Multi-discipline site package', body: 'Combined equipment rental, technical manpower and site logistics supplied to an EPC contractor as a single call-off arrangement across a construction programme.' },
  { title: 'Field Maintenance Contract', meta: 'Long-term resourcing', body: 'Resident maintenance crews and supervision provided under a term contract, with equipment availability and response times defined in the scope of work.' },
  { title: 'Chemicals & Instrumentation Supply', meta: 'Recurring supply scope', body: 'Oilfield and industrial chemicals, instrumentation and flow measurement equipment supplied to site against the client treatment and measurement programme.' },
];

const advantages = [
  { icon: <Package className="w-7 h-7" />, title: 'One Purchase Order, One Interface', body: 'Equipment, manpower, chemicals and logistics contracted on a single PO. Procurement stops coordinating four vendors to complete one scope.' },
  { icon: <Users className="w-7 h-7" />, title: 'Our Crews, Our Equipment', body: 'Operating personnel and equipment come from the same organisation. Accountability is single-point from mobilisation through demobilisation.' },
  { icon: <Timer className="w-7 h-7" />, title: 'Built for Fixed Windows', body: 'Shutdown and commissioning work is planned against a date that does not move. Resourcing, spares and standby cover are set to that constraint.' },
  { icon: <Layers3 className="w-7 h-7" />, title: 'Group Equipment Depth', body: 'Access to the wider EnergiPro Group fleet and supply chain across Saudi Arabia, the UAE and Egypt when a scope exceeds local availability.' },
  { icon: <ShieldCheck className="w-7 h-7" />, title: 'Documented, Audit-Ready Execution', body: 'Inspection records, calibration certificates, competency matrices and maintenance logs maintained and available for client audit.' },
  { icon: <Handshake className="w-7 h-7" />, title: 'Egyptian Base, Regional Standard', body: 'Locally based crews and logistics operating to the same procedures, HSE expectations and documentation standard applied across the group.' },
];

const certifications = [
  { label: 'Legal Entity', value: 'Petro Nix Energy Services L.L.C.', note: 'Egyptian limited liability company registered with the Ministry of Finance — Egyptian Tax Authority. Registered office: New Maadi, Cairo, Egypt. Tax card issued and current.' },
  { label: 'Licensed Activity', value: 'Activity Code 0910', note: 'Support service activities for petroleum and natural gas extraction — the registered scope under which the company contracts in Egypt. Operating since January 2025.' },
  { label: 'Group IMS', value: 'ISO 9001 · 14001 · 45001 Framework', note: 'Operating within the EnergiPro Group integrated management system for quality, environment and occupational health & safety.' },
  { label: 'HSE', value: 'Permit-to-Work & JSA Discipline', note: 'Job safety analysis, permit-to-work compliance, toolbox talks and incident reporting built into the execution workflow on every scope.' },
  { label: 'Equipment', value: 'Inspection & Calibration Records', note: 'Rental and process equipment maintained with current inspection, pressure test and calibration certification available for audit.' },
  { label: 'Personnel', value: 'Competency & Certification Matrices', note: 'Technical manpower supplied against documented trade certification, medical fitness and site-specific induction requirements.' },
  { label: 'Records', value: 'Prequalification Pack on Request', note: 'Company documents, insurance certificates, HSE statistics and client references issued to procurement on request.' },
];

export default function Petronix() {
  return (
    <Layout>
    <main className="bg-white">
      <SeoHead
        title="Petronix Energy Services Egypt | Oilfield Equipment & Field Services"
        description="Petronix Energy Services is the Egyptian energy-services arm of EnergiPro Group — oilfield equipment rental, industrial supply, technical manpower, maintenance, and field logistics across Egypt."
        canonicalPath="/companies/petronix"
      />
      {/* ---------- 1. HERO ---------- */}
      <CompanyHero
        eyebrow={['ENERGY SERVICES', 'OILFIELD SUPPORT', 'FIELD SOLUTIONS']}
        icon={<Zap className="w-6 h-6" />}
        heroLogo="/images/logos/petronix-logo-light-bg.png"
        title="Supporting Energy Operations Across Egypt"
        tagline="Field Solutions for Energy and Industry"
        lead="Petronix Energy Services is the Egyptian energy-services arm of EnergiPro Group — supplying equipment, technical manpower, field services and project support to oil and gas operators, service companies and EPC contractors across Egypt."
        sub="Equipment rental, industrial supply, process and fluid handling packages, maintenance and shutdown resourcing, commissioning support and field logistics — contracted through one commercial interface."
        primary={{ label: 'Request Technical Proposal', to: '/contact' }}
        secondary={{ label: 'Talk to Operations', to: '/contact' }}
        badges={[
          'Equipment + Crews on One PO',
          'Shutdown & Commissioning Resourcing',
          'Group Fleet Access — KSA · UAE · Egypt',
          'Audit-Ready Documentation',
        ]}
      />

      {/* ---------- 2. COMPANY OVERVIEW ---------- */}
      <Section tone="white">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Company Overview"
              title="The Egyptian Energy-Services Arm of EnergiPro Group"
              description="Petronix Energy Services applies the EnergiPro Solutions operating model — owned equipment, directly employed crews and a single commercial interface — to the Egyptian energy and industrial market."
            />
            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                Most delay on an Egyptian oilfield or energy-facility scope is not technical. It is the
                gap between the equipment vendor, the manpower supplier, the chemicals supplier and
                the transport contractor — four organisations, four schedules, and no single party
                accountable for the date. Petronix exists to close that gap.
              </p>
              <p>
                The company supplies process and rental equipment, industrial materials, oilfield
                chemicals, instrumentation and flow measurement, together with the technical
                manpower to operate and maintain it. Scopes are contracted as complete packages:
                equipment, crews, consumables and logistics under one purchase order, with one
                point of responsibility from mobilisation to demobilisation.
              </p>
              <p>
                Execution follows the group standard — documented procedures, permit-to-work
                compliance, job safety analysis before every activity, and traceable inspection and
                calibration records. Reporting is issued in a form that goes directly into the
                client's own documentation without being rebuilt.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 grid sm:grid-cols-2 gap-5">
            <StatCard icon={<MapPin className="w-6 h-6" />} label="Operating Base" value="Cairo, Egypt" note="Equipment yard and operations base serving Egyptian field and plant locations" />
            <StatCard icon={<Layers3 className="w-6 h-6" />} label="Service Lines" value="9" note="Rental · Supply · Process · Manpower · Maintenance · Shutdown · Commissioning · Logistics · Engineering" />
            <StatCard icon={<Building2 className="w-6 h-6" />} label="Group Network" value="3 Countries" note="Saudi Arabia · United Arab Emirates · Egypt" />
            <StatCard icon={<FileCheck2 className="w-6 h-6" />} label="Commercial Model" value="Single PO" note="Equipment, crews, chemicals and logistics on one contract" />
          </div>
        </div>
      </Section>

      {/* ---------- 3. CORE SERVICES ---------- */}
      <Section tone="muted">
        <SectionHeading
          eyebrow="Core Services"
          title="Energy & Industrial Service Lines"
          description="Six service lines that can be contracted individually or combined into a single integrated field or plant support package."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreServices.map((s) => (
            <FeatureCard key={s.title} {...s} />
          ))}
        </div>
      </Section>

      {/* ---------- 4. INDUSTRIES SERVED ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Industries Served"
          title="Sectors We Support"
          description="Petronix is licensed and registered in Egypt for support service activities to petroleum and natural gas extraction — activity code 0910. Everything below sits inside that scope."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((i) => (
            <div
              key={i.title}
              className="bg-white border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-orange-600/50"
            >
              <div className="text-orange-600 mb-4">{i.icon}</div>
              <h3 className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-2">
                {i.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{i.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 border-l-4 border-orange-600 bg-slate-50 p-7 md:p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-600 mb-3">
            Beyond the Egyptian Licensed Scope
          </p>
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">
            Downstream, Power and Industrial Scopes — Contracted Through the Group
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed max-w-4xl">
            Refining and petrochemical turnarounds, power generation, utilities, terminals and
            general industrial maintenance are contracted through EnergiPro Group rather than under
            the Egyptian licence, with Petronix providing the local crews, fleet and field logistics.
            Clients get the same single commercial interface — and a contracting structure that
            matches what each entity is registered to perform.
          </p>
        </div>
      </Section>

      {/* ---------- 5. KEY CAPABILITIES ---------- */}
      <Section tone="gradient">
        <SectionHeading
          eyebrow="Key Capabilities"
          title="Equipment, Technical Services & Project Support"
          description="What Petronix can put on site, operate, maintain and document — grouped by how procurement contracts it."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {capabilities.map((c) => (
            <ListPanel key={c.title} title={c.title} items={c.items} icon={c.icon} />
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Fan className="w-5 h-5" />, t: 'Generators & Compressors' },
            { icon: <Container className="w-5 h-5" />, t: 'Tank Solutions & Storage' },
            { icon: <FlaskConical className="w-5 h-5" />, t: 'Oilfield Chemicals' },
            { icon: <Activity className="w-5 h-5" />, t: 'Flow Measurement' },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-3 bg-white border border-slate-200 px-5 py-4">
              <span className="text-orange-600">{x.icon}</span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{x.t}</span>
            </div>
          ))}
        </div>
      </Section>

      <AssuranceStrip text="Owned equipment. Our crews. One purchase order. Single point of accountability." />

      {/* ---------- 6. TYPICAL PROJECTS ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Typical Projects"
          title="Representative Scopes of Work"
          description="Typical scope shapes Petronix is contracted for. Executed project references and client details are issued during prequalification."
        />
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((p) => (
            <div
              key={p.title}
              className="bg-white border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-orange-600/50"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-600 mb-2">
                {p.meta}
              </p>
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">
                {p.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-slate-400 max-w-3xl leading-relaxed">
          Scopes shown are representative of Petronix service packages. Project-specific references
          and documentation are provided on request during vendor prequalification.
        </p>
      </Section>

      {/* ---------- 7. GEOGRAPHIC COVERAGE ---------- */}
      <Section tone="dark">
        <SectionHeading
          dark
          eyebrow="Geographic Coverage"
          title="Egypt-Wide Field and Plant Support"
          description="Based in Cairo, mobilising to field, plant and terminal locations across Egypt — with group equipment yards in Dammam and Dubai available where a scope exceeds local fleet."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: 'Cairo — Operating Base', d: 'Operations base and equipment yard. Commercial, engineering and mobilisation functions run from here.' },
            { t: 'Egyptian Field Locations', d: 'Mobilisation to onshore concessions, gathering stations, gas processing facilities and rig sites — Western Desert, Eastern Desert, Nile Delta and Gulf of Suez onshore.' },
            { t: 'Group Yards — Dammam & Dubai', d: 'Access to EnergiPro Group equipment yards in Dammam (KSA) and Dubai (UAE) for fleet depth, cross-border scopes and specialist packages.' },
          ].map((g) => (
            <div key={g.t} className="border border-white/15 bg-white/5 p-7">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-orange-500" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-white">
                  {g.t}
                </h3>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{g.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- 8. COMPETITIVE ADVANTAGES ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Competitive Advantages"
          title="Why Clients Contract Petronix"
          description="Not an equipment broker and not a labour agency — an operating company that supplies the equipment, runs it, and signs for the result."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((a) => (
            <FeatureCard key={a.title} icon={a.icon} title={a.title} body={a.body} />
          ))}
        </div>
      </Section>

      {/* ---------- 9. CERTIFICATIONS ---------- */}
      <Section tone="muted">
        <div className="flex justify-center mb-8">
          <img
            src="/images/logos/petronix-logo-light-bg.png"
            alt="Petronix logo"
            className="w-40 md:w-44 h-auto object-contain"
          />
        </div>
        <SectionHeading
          eyebrow="Certifications & Compliance"
          title="Governance, HSE & Documentation"
          description="The registration, management-system and record-keeping framework under which Petronix contracts and executes in Egypt."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((c) => (
            <div key={c.value} className="bg-white border border-slate-200 p-7">
              <div className="flex items-center gap-2.5 mb-4">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  {c.label}
                </span>
              </div>
              <h3 className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-3">
                {c.value}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- 10. CONTACT INFORMATION ---------- */}
      <Section tone="white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Contact"
              title="Speak to Petronix Operations"
              description="Send the scope, the dates and the site. Operations reviews availability and mobilisation feasibility before any commercial response is issued."
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { icon: <MapPin className="w-5 h-5" />, label: 'Office', value: 'Petronix Energy Services', note: 'New Maadi, Cairo, Egypt' },
              { icon: <Phone className="w-5 h-5" />, label: 'Telephone', value: '+20 115 696 9766', note: 'Operations and mobilisation enquiries' },
              { icon: <Mail className="w-5 h-5" />, label: 'Email', value: 'gm@petronix-eg.com', note: 'Technical enquiries and proposal requests' },
              { icon: <Globe className="w-5 h-5" />, label: 'Web', value: 'www.energiprogroup.com', note: 'A member of EnergiPro Group' },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-slate-200 p-7">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="text-orange-600">{c.icon}</span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    {c.label}
                  </span>
                </div>
                <p className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-2 break-words">
                  {c.value}
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- 11. CTA BANNER ---------- */}
      <CTABanner
        heading="Put One Contractor on the Whole Scope"
        sub="Equipment, crews, chemicals and logistics under a single purchase order — mobilised to your site in Egypt against your date, not ours."
        primary={{ label: 'Request Technical Proposal', to: '/contact' }}
        secondary={{ label: 'Discuss a Shutdown Scope', to: '/contact' }}
      />

      {/* ---------- 12. MEMBER OF ENERGIPRO GROUP ---------- */}
      <GroupMemberSection current="petronix" />
    </main>
    </Layout>
  );
}
